// The offer calculator's intro film and the markup pieces it shares with the calculator: icons, escaping, money,
// tax wording and tags. The calculator (assets/angebot.js) plays the film on its intro; the services page
// (assets/services.js) plays it in place of a picture, with the calculator's settings read from the offer page. The
// on-demand funnel (assets/on-demand.js) plays its own scenes in the same player.
// Needs assets/angebot-core.js first.
(() => {
  'use strict';

  const O = window.MixedownOffer;
  if (!O) return;

  const ICONS = {
    check: '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M2.5 8.5l3.5 3.5 7.5-8"/></svg>',
    cross: '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M4 4l8 8M12 4l-8 8"/></svg>',
    minus: '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M3 8h10"/></svg>',
    plus: '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M3 8h10M8 3v10"/></svg>',
    info: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="9"/><path d="M12 11v6M12 7.5v.5"/></svg>',
    clock: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>',
    tag: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M3 12.5V4h8.5l9 9-8.5 8.5z"/><circle cx="7.5" cy="8.5" r="1.5"/></svg>',
    download: '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M8 2v9M4 7l4 4 4-4M2 14h12"/></svg>',
    share: '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M8 10V2M4.5 5.5L8 2l3.5 3.5M3 9v5h10V9"/></svg>',
    edit: '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M10.5 2.5l3 3-8 8h-3v-3zM8.5 4.5l3 3"/></svg>',
    arrow: '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M2 8h12M9 3l5 5-5 5"/></svg>',
    sign: '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M1.5 10.5c1.5-2.5 2.8-7 4.5-7 1.6 0-.6 7 1 7 1.3 0 2-3.2 3.2-3.2 1 0 .6 2.6 1.7 2.6.8 0 1.5-.7 2.6-1.3M1.5 13.5h13"/></svg>',
    external: '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M9.5 2.5h4v4M13.5 2.5L7.5 8.5M11.5 9.5v4h-9v-9h4"/></svg>',
    lock: '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><rect x="3" y="7" width="10" height="7" rx="1.5"/><path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2"/></svg>'
  };

  const escapeHtml = value => String(value ?? '').replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);
  const money = cents => O.formatMoney(cents);
  const taxSuffix = (q, short = false) => {
    if (q.tax.note === 'export') return short ? 'ohne USt.' : 'ohne USt. (Ausfuhrlieferung), ggf. zzgl. Zoll und Einfuhrabgaben im Zielland';
    if (q.tax.note === 'reverse') return short ? 'ohne USt.' : 'ohne USt., steuerfreie innergemeinschaftliche Lieferung';
    return short ? 'netto' : `netto, zzgl. ${O.formatPercent(q.tax.rate)} USt.`;
  };
  const WEEKDAYS = ['So.', 'Mo.', 'Di.', 'Mi.', 'Do.', 'Fr.', 'Sa.'];
  const pieces = count => `${O.formatCount(count)} ${count === 1 ? 'Teil' : 'Teile'}`;
  const firstColor = product => (product.colors.find(color => O.sizesFor(product, color.name).length) || product.colors[0]).name;
  const extraTag = (extra, who) => (extra.for.includes(who) ? `<span class="mxd-offer__tag${extra.tone ? ` mxd-offer__tag--${extra.tone}` : ''}">${escapeHtml(extra.tag || 'Empfohlen')}</span>` : '');
  const EXPRESS_TAG = '<span class="mxd-offer__tag mxd-offer__tag--blue">Express</span>';

  // What an order is before anyone picks anything: nobody yet, nothing in it, sent home, printed when ready.
  const blankOrder = config => ({
    who: '',
    selected: [],
    lines: [],
    design: 'ready',
    extras: [],
    timing: 'open',
    date: '',
    express: false,
    pickup: false,
    country: config.home,
    vatId: ''
  });

  // The film: a small window plays the calculator, a little faster than by hand. A team orders shirts, a pointer glides
  // over, rests on each pick and clicks it, and a live price follows; every number comes from a real quote for that
  // sample order. It ends on the thank-you screen with the PDF and the link at hand.
  const REEL_MOVE_MS = 600;
  const REEL_TAP_MS = 100;
  const REEL_REST_MS = 180;
  const REEL_COUNT_MS = 700;
  const REEL_WAIT_MS = 300;
  // The film's order: the most pieces whose net bill, extra included, stays under this many cents.
  const REEL_BUDGET = 30000;
  const REEL_FIRST_PIECES = 11;
  const REEL_MAX_PIECES = 200;
  // The film for the calculator's settings (normalized by the core), or null without products. It moves on from scene
  // to scene while shown() is true and the page is visible; play() starts it again after a pause.
  const create = (config, { shown = () => true } = {}) => {
    const who = 'team';
    const first = list => [...list].sort((a, b) => Number(b.for.includes(who)) - Number(a.for.includes(who)));
    const catalogue = first(config.products);
    const products = catalogue.slice(0, 8);
    if (!products.length) return null;
    const product = products[0];
    const color = firstColor(product);
    const sizes = O.sizesFor(product, color).slice(0, 4);
    // The paid extras only: free services like the shop are offered apart from them.
    const extras = config.extras.filter(extra => extra.price);
    const today = new Date();
    const orderOf = (amounts, filled) => ({
      ...blankOrder(config),
      who,
      lines: [{ product: product.id, color, total: sizes.length ? null : amounts[0], sizes: Object.fromEntries(sizes.slice(0, filled).map((size, index) => [size, amounts[index]])) }],
      design: 'ready'
    });
    const quoteWith = (amounts, picked, filled = amounts.length) => O.quote(config, { ...orderOf(amounts, filled), extras: picked.map(extra => extra.id) }, today);
    // The thrifty order a team would really place: the extra that costs it least.
    const pickFor = count => extras.reduce((low, extra) => {
      const cost = entry => entry.price * (entry.unit === 'piece' ? count : 1);
      return !low || cost(extra) < cost(low) ? extra : low;
    }, null);
    // The pieces split over the first three sizes, as many as keep the whole bill under the budget.
    const spread = Math.min(3, sizes.length);
    const split = count => (spread ? Array.from({ length: spread }, (_, index) => Math.floor(count / spread) + (index < count % spread ? 1 : 0)) : [count]);
    const orderFor = count => {
      const pick = pickFor(count);
      return { amounts: split(count), pick, q: quoteWith(split(count), pick ? [pick] : []) };
    };
    let order = orderFor(REEL_FIRST_PIECES);
    for (let count = REEL_FIRST_PIECES + 1; count <= REEL_MAX_PIECES; count += 1) {
      const next = orderFor(count);
      if (next.q.net >= REEL_BUDGET) break;
      order = next;
    }
    const { amounts, pick, q } = order;
    // One quote per filled size, so pieces, discount and price grow with each of them.
    const filling = amounts.map((amount, index) => quoteWith(amounts, [], index + 1));
    const counted = filling[filling.length - 1];
    // One click on a size's plus sets its amount; the pointer sets off to the next size a moment later.
    const tapAt = amounts.map((_, index) => 750 + index * (REEL_MOVE_MS + REEL_REST_MS + REEL_WAIT_MS));

    // The pieces fill a track up to where the discount starts (or up to the pieces, past the last step): a knob rides
    // its end and a chip tells what each size added.
    const end = q.next ? q.next.from : q.pieces;
    const track = config.tiers.some(([, percent]) => percent > 0) ? '<div class="mxd-offer__reel-track"><span></span><i></i><b></b></div>' : '';

    const media = (entry, inside = '') => `<span class="mxd-offer__reel-media">${entry.image ? `<img src="${escapeHtml(entry.image)}" alt="" loading="lazy">` : ''}${inside}</span>`;
    // A tile the pointer clicks `at` ms into its scene, moving the live price to `total`; without `at` it is left alone.
    const tile = (at, html, { total = null, modifier = '', delay = null } = {}) => `<div class="mxd-offer__reel-tile${modifier ? ` mxd-offer__reel-tile--${modifier}` : ''}"${delay === null ? '' : ` style="--reel-delay: ${delay}"`}${at ? ` data-reel-at="${at}"` : ''}${total === null ? '' : ` data-reel-live="${total}"`}>${html}</div>`;
    const arrival = q.delivery.arrival;
    const rows = [...q.rows.map(row => [`${row.count} × ${row.title}`, row.total]), ...q.extras.map(extra => [extra.title, extra.total])];
    const scenes = [
      {
        title: 'Was darf’s sein?',
        note: catalogue.length > products.length ? `+${catalogue.length - products.length} weitere` : '',
        ms: 2900,
        html: `<div class="mxd-offer__reel-grid mxd-offer__reel-grid--products">${products.map((entry, index) => tile(index === 0 ? 1500 : 0, `${media(entry, entry.badge ? `<span class="mxd-offer__tag">${escapeHtml(entry.badge)}</span>` : '')}<span>${escapeHtml(entry.title)}</span>`, { delay: index })).join('')}</div>`
      },
      {
        title: 'Wie viele?',
        ms: tapAt[amounts.length - 1] + 1400,
        html: `<div class="mxd-offer__reel-product">${media(product)}<span>${escapeHtml(product.title)} · ${escapeHtml(color)}</span></div>
          <div class="mxd-offer__reel-sizes">${(sizes.length ? sizes : ['Stück']).map((size, index) => tile(index < amounts.length ? tapAt[index] : 0, `<span class="mxd-offer__reel-size-name">${escapeHtml(size)}</span><span class="mxd-offer__reel-stepper"><i>${ICONS.minus}</i><strong data-reel-amount="${amounts[index] || 0}">0</strong><i data-reel-aim>${ICONS.plus}</i></span>`, { total: index < amounts.length ? filling[index].goodsPerPiece : null, modifier: 'size' })).join('')}</div>
          <div class="mxd-offer__reel-tiers">
            <p><span><strong data-reel-pieces>0</strong> Teile</span><small></small></p>
            ${track}
          </div>`,
        live: [0, 0]
      },
      {
        title: 'Noch was dazu?',
        ms: 2200,
        html: `<div class="mxd-offer__reel-list">${extras.map(extra => tile(extra === pick ? 950 : 0, `<span>${escapeHtml(extra.title)}${extraTag(extra, who)}</span><i>${ICONS.check}</i>`)).join('')}${config.express ? `<div class="mxd-offer__divider"><span>Noch schneller?</span></div>${tile(0, `<span>Priority-Queue${EXPRESS_TAG}</span><i>${ICONS.check}</i>`)}` : ''}</div>`,
        live: [counted.goodsPerPiece, counted.goodsPerPiece]
      },
      {
        title: 'Dein Angebot',
        ms: 3700,
        html: `<div class="mxd-offer__reel-offer">
            <div class="mxd-offer__reel-price"><strong data-reel-count="${q.net}" data-reel-money>0</strong>${q.savings ? `<em>Du sparst ${money(q.savings)}</em>` : ''}</div>
            <span>${taxSuffix(q, true)} · ${pieces(q.pieces)} · ${money(q.perPiece)} / Teil${q.tier.percent ? ` · −${O.formatPercent(q.tier.percent)}` : ''}</span>
            <ul class="mxd-offer__reel-rows">${rows.map(([title, total], index) => `<li style="--reel-delay: ${index}"><span>${escapeHtml(title)}</span><span>${money(total)}</span></li>`).join('')}</ul>
            <div class="mxd-offer__reel-end">
              <div class="mxd-offer__reel-button" data-reel-at="2500"><span>Angebot akzeptieren ${ICONS.sign}</span><span>Akzeptiert ${ICONS.check}</span></div>
              <span>${ICONS.clock}Bei dir ${WEEKDAYS[arrival.getDay()]} ${O.formatDate(arrival).slice(0, 6)}</span>
            </div>
          </div>`
      },
      {
        title: 'Danke! Wir legen los.',
        ms: 4200,
        html: `<div class="mxd-offer__reel-stats">${[[O.offerNumber(today), 'Angebotsnummer'], [money(q.net), taxSuffix(q, true)], [O.formatCount(q.pieces), 'Teile']].map(([value, label]) => `<div><strong>${escapeHtml(value)}</strong><span>${escapeHtml(label)}</span></div>`).join('')}</div>
          <div class="mxd-offer__reel-tools">
            <div class="mxd-offer__reel-tool" data-reel-at="1250"><span>${ICONS.download}PDF</span><span>${ICONS.check}Gespeichert</span></div>
            <div class="mxd-offer__reel-tool" data-reel-at="2200"><span>${ICONS.share}Teilen</span><span>${ICONS.check}Link kopiert</span></div>
          </div>
          <ol class="mxd-offer__reel-steps">${['Druckvorschau', 'Freigabe und Zahlung', 'Produktion und Versand'].map((title, index) => `<li style="--reel-delay: ${index}"><b>${index + 1}</b>${title}</li>`).join('')}</ol>`
      }
    ];
    const player = film(scenes, {
      shown,
      // The sizes scene starts with an empty track, or a full one when nothing moves.
      enter: (scene, still) => {
        if (scene.contains(tiers)) showFilled(still ? amounts.length : 0, false);
      },
      // A size's plus was clicked: pieces, discount and track follow.
      tapped: target => showFilled(target.parentElement.querySelectorAll('.is-on').length, true)
    });
    const tiers = player.element.querySelector('.mxd-offer__reel-tiers');
    const piecesTotal = tiers.querySelector('[data-reel-pieces]');

    // The pieces so far, where the discount starts and the track filled up to them.
    const showFilled = (filled, animate) => {
      const quote = filled ? filling[filled - 1] : null;
      const total = quote ? quote.pieces : 0;
      if (animate) count(piecesTotal, Number(piecesTotal.dataset.reelPieces), total, O.formatCount);
      else {
        piecesTotal.reelRun = null;
        piecesTotal.textContent = O.formatCount(total);
      }
      piecesTotal.dataset.reelPieces = total;
      const next = (quote || filling[0]).next;
      tiers.querySelector('small').textContent = next ? `Mengenrabatt ab ${O.formatCount(next.from)} Teilen` : '';
      const bar = tiers.querySelector('.mxd-offer__reel-track');
      if (!bar) return;
      bar.classList.toggle('is-moving', animate);
      bar.style.setProperty('--reel-fill', Math.min(1, total / end));
      bar.classList.remove('is-bumped');
      if (!animate || !filled) return;
      bar.querySelector('b').textContent = `+${O.formatCount(total - (filled > 1 ? filling[filled - 2].pieces : 0))}`;
      void bar.offsetWidth;
      bar.classList.add('is-bumped');
    };
    return player;
  };

  // Counts ease out from one number to the next; a newer count on the same element takes over.
  const count = (target, from, to, format) => {
    const run = {};
    target.reelRun = run;
    const began = performance.now();
    const tick = now => {
      if (target.reelRun !== run) return;
      const t = Math.min(1, (now - began) / REEL_COUNT_MS);
      target.textContent = format(Math.round(from + (to - from) * (1 - (1 - t) ** 3)));
      if (t < 1) requestAnimationFrame(tick);
    };
    target.textContent = format(from);
    requestAnimationFrame(tick);
  };

  // The player around a film's scenes: { title, note?, ms, html, live? } each. A tile with data-reel-at is clicked that
  // many ms into its scene; with data-reel-live it moves the live figure (cents, followed by `unit`) there, and a
  // [data-reel-amount] inside it is set by one click on its plus. `live` is [shown, counted from] for a scene that
  // shows the figure. enter(scene, still) prepares a scene as it comes on, tapped(target) follows a plus clicked. It
  // moves on from scene to scene while shown() is true and the page is visible; play() starts it again after a pause.
  const film = (scenes, { shown = () => true, unit = '/ Teil', once = false, enter = () => {}, tapped = () => {} } = {}) => {
    const element = document.createElement('div');
    element.className = 'mxd-offer__reel';
    element.setAttribute('aria-hidden', 'true');
    element.style.setProperty('--reel-count', `${REEL_COUNT_MS}ms`);
    element.innerHTML = `
      <div class="mxd-offer__reel-window">
        <div class="mxd-offer__reel-stage">
          ${scenes.map(scene => `<div class="mxd-offer__reel-scene"><p class="mxd-offer__reel-title">${scene.title}${scene.note ? `<span class="mxd-offer__reel-note">${scene.note}</span>` : ''}</p>${scene.html}</div>`).join('')}
          <div class="mxd-offer__reel-live"><strong></strong><small>${unit}</small></div>
          <div class="mxd-offer__reel-pointer"><svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M3 2v17.5l4.6-4.3 3.2 7 3-1.3-3.1-6.9 6.3-.4z"/></svg></div>
        </div>
        <div class="mxd-offer__reel-foot">
          <span class="mxd-offer__reel-badge"><svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M5 3.2l7.5 4.8L5 12.8z"/></svg>Vorschau</span>
          <div class="mxd-offer__reel-bar">
            ${scenes.map(scene => `<span class="mxd-offer__reel-segment" style="--reel-scene: ${scene.ms}ms"><span></span></span>`).join('')}
          </div>
          <span class="mxd-offer__reel-time"></span>
        </div>
      </div>`;
    const sceneElements = [...element.querySelectorAll('.mxd-offer__reel-scene')];
    const segments = [...element.querySelectorAll('.mxd-offer__reel-segment')];
    const liveBox = element.querySelector('.mxd-offer__reel-live');
    const liveTotal = liveBox.querySelector('strong');
    const pointer = element.querySelector('.mxd-offer__reel-pointer');
    const timeBox = element.querySelector('.mxd-offer__reel-time');
    const length = scenes.reduce((sum, scene) => sum + scene.ms, 0);
    const clock = ms => `${Math.floor(ms / 60000)}:${String(Math.floor(ms / 1000) % 60).padStart(2, '0')}`;
    const still = window.matchMedia('(prefers-reduced-motion: reduce)');

    let index = -1;
    let timer = null;
    let ended = false;
    let beats = [];
    let liveCents = 0;
    const later = (ms, action) => beats.push(setTimeout(action, ms));
    // The pointer glides on a gentle arc, rests on the target for a moment and clicks it; the target answers. Short
    // ways take less time, and every glide ends REEL_MOVE_MS after it was set off.
    const glide = (x, y) => {
      const width = pointer.parentElement.clientWidth;
      if (!width) return;
      const from = new window.DOMMatrixReadOnly(window.getComputedStyle(pointer).transform);
      const duration = Math.round(REEL_MOVE_MS * Math.min(1, 0.45 + (0.8 * Math.hypot(x - from.e, y - from.f)) / width));
      const bend = (x - from.e) * 0.12 + (y - from.f) * 0.12;
      const control = [(from.e + x) / 2 + bend, (from.f + y) / 2 - Math.abs(bend)];
      const frames = Array.from({ length: 17 }, (_, i) => {
        const t = i / 16;
        const at = (a, b, c) => (1 - t) ** 2 * a + 2 * (1 - t) * t * b + t ** 2 * c;
        return { transform: `translate(${at(from.e, control[0], x)}px, ${at(from.f, control[1], y)}px)` };
      });
      pointer.getAnimations().forEach(animation => animation.cancel());
      pointer.style.transform = frames[frames.length - 1].transform;
      pointer.animate(frames, { duration, delay: REEL_MOVE_MS - duration, fill: 'backwards', easing: 'cubic-bezier(0.5, 0, 0.2, 1)' });
    };
    const follow = target => {
      count(liveTotal, liveCents, Number(target.dataset.reelLive), money);
      liveCents = Number(target.dataset.reelLive);
    };
    // One click on a size's plus sets its whole amount; then what follows from it.
    const tap = (target, amount) => {
      const press = [{ transform: 'scale(0.82)' }, { transform: 'none' }];
      amount.textContent = O.formatCount(Number(amount.dataset.reelAmount));
      amount.animate([{ transform: 'scale(1.35)' }, { transform: 'none' }], { duration: 320, easing: 'cubic-bezier(0.3, 1.6, 0.5, 1)' });
      target.querySelector('[data-reel-aim]').animate(press, { duration: REEL_TAP_MS, easing: 'ease-out' });
      pointer.querySelector('svg').animate(press, { duration: REEL_TAP_MS, easing: 'ease-out' });
      tapped(target);
      follow(target);
    };
    const click = target => {
      const at = Number(target.dataset.reelAt);
      const aim = target.querySelector('[data-reel-aim]') || target;
      later(at - REEL_REST_MS - REEL_MOVE_MS, () => {
        // Measured against its own scene, which may still be sliding in.
        const box = aim.getBoundingClientRect();
        const scene = target.closest('.mxd-offer__reel-scene').getBoundingClientRect();
        // A button inside a tile, like a stepper's plus, is hit in its middle.
        const [across, down] = aim === target ? [0.55, 0.6] : [0.5, 0.5];
        glide(box.left - scene.left + box.width * across, box.top - scene.top + box.height * down);
      });
      later(at - REEL_REST_MS, () => target.classList.add('is-hover'));
      later(at, () => {
        target.classList.remove('is-hover');
        target.classList.add('is-on');
        const amount = target.querySelector('[data-reel-amount]');
        if (amount) tap(target, amount);
        else if ('reelLive' in target.dataset) follow(target);
        pointer.classList.remove('is-clicking');
        void pointer.offsetWidth;
        pointer.classList.add('is-clicking');
      });
    };
    const showScene = next => {
      index = next;
      beats.forEach(clearTimeout);
      beats = [];
      // The running time ticks on like a player's, from where this scene starts.
      const before = scenes.slice(0, index).reduce((sum, entry) => sum + entry.ms, 0);
      for (let at = 0; at < scenes[index].ms; at += 1000) later(at, () => {
        timeBox.textContent = `${clock(before + at)} / ${clock(length)}`;
      });
      const scene = sceneElements[index];
      scene.querySelectorAll('.is-on, .is-hover').forEach(target => target.classList.remove('is-on', 'is-hover'));
      scene.querySelectorAll('[data-reel-amount]').forEach(amount => {
        amount.reelRun = null;
        amount.textContent = still.matches ? O.formatCount(Number(amount.dataset.reelAmount)) : '0';
      });
      enter(scene, still.matches);
      sceneElements.forEach((entry, i) => {
        entry.classList.toggle('is-active', i === index);
        entry.classList.toggle('is-leaving', i === (index + sceneElements.length - 1) % sceneElements.length && i !== index);
      });
      segments.forEach((segment, i) => {
        segment.classList.toggle('is-done', i < index);
        segment.classList.remove('is-active');
      });
      void segments[index].offsetWidth;
      segments[index].classList.add('is-active');

      scene.querySelectorAll('[data-reel-count]').forEach(target => {
        const format = 'reelMoney' in target.dataset ? money : O.formatCount;
        const to = Number(target.dataset.reelCount);
        if (still.matches) target.textContent = format(to);
        else {
          target.textContent = format(0);
          later(REEL_WAIT_MS, () => count(target, 0, to, format));
        }
      });
      // The live price shows where the scene starts, then follows each pick.
      const range = scenes[index].live;
      liveBox.classList.toggle('is-shown', Boolean(range));
      if (range) {
        liveTotal.reelRun = null;
        liveTotal.textContent = money(range[0]);
        liveCents = range[1];
      }
      if (!still.matches) scene.querySelectorAll('[data-reel-at]').forEach(click);
    };
    const step = () => {
      timer = null;
      if (!shown() || document.hidden || !element.isConnected) return;
      // A film that runs once stops on its last scene, filled and at its full time, to be read at leisure.
      if (once && index === sceneElements.length - 1) {
        ended = true;
        segments[index].classList.replace('is-active', 'is-done');
        timeBox.textContent = `${clock(length)} / ${clock(length)}`;
        return;
      }
      showScene((index + 1) % sceneElements.length);
      timer = setTimeout(step, scenes[index].ms);
    };
    return {
      element,
      play() {
        if (timer || ended) return;
        if (still.matches) {
          showScene(sceneElements.length - 1);
          return;
        }
        step();
      }
    };
  };

  window.MixedownOfferReel = { ICONS, escapeHtml, money, taxSuffix, WEEKDAYS, pieces, firstColor, extraTag, EXPRESS_TAG, blankOrder, create, film };
})();
